/**
 * Tactile layer. iOS has no navigator.vibrate (WebKit opposes it), so the notch "feel" is
 * audio tick + visual snap; Android Chromium gets a real vibration as progressive enhancement.
 */
let ctx: AudioContext | null = null

export function isMuted(): boolean { return localStorage.getItem('wayoff.mute') === '1' }
export function setMuted(m: boolean) { localStorage.setItem('wayoff.mute', m ? '1' : '0') }

/** Call from inside a user gesture (pointerdown) — required to unlock audio on iOS. */
export function ensureAudio() {
  if (isMuted()) return
  try {
    ctx ??= new (window.AudioContext || (window as any).webkitAudioContext)()
    if (ctx.state === 'suspended') void ctx.resume()
  } catch { /* no audio */ }
}
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && ctx?.state === 'suspended') void ctx.resume() // iOS suspends on background
  })
}

/** Short filtered click — the detent tick. */
export function tick(strong = false) {
  if (isMuted() || !ctx || ctx.state !== 'running') return
  const t = ctx.currentTime
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'square'
  osc.frequency.setValueAtTime(strong ? 1900 : 1400, t)
  osc.frequency.exponentialRampToValueAtTime(strong ? 700 : 500, t + 0.02)
  gain.gain.setValueAtTime(strong ? 0.09 : 0.05, t)
  gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.045)
  osc.connect(gain).connect(ctx.destination)
  osc.start(t); osc.stop(t + 0.05)
}

/** Android Chromium only; return value is non-authoritative — never branch UX on it. */
export function buzz(ms = 15) {
  try { if ('vibrate' in navigator) navigator.vibrate(ms) } catch { /* no-op */ }
}

export function reducedMotion(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches
}
